<script lang="ts" setup>
import LatestPurchasesRoblox from "@/Components/LatestPurchasesRoblox.vue";
import FAQAccordion from "@/Components/FAQAccordion.vue";
import RobloxDraw from "@/Components/RobloxDraw.vue";
import RobloxMarketplacePromo from "@/Components/RobloxMarketplacePromo.vue";
import { computed, ref } from "vue";
import { LOCALES, tgUrl, vkUrl } from "@/constants.js";
import debounce from "lodash-es/debounce.js";
import { route } from "ziggy-js";
import { Head, router, usePage } from "@inertiajs/vue3";
import { trans } from "laravel-vue-i18n";
import axios from "axios";
import RobloxLayout from "@/Layouts/RobloxLayout.vue";
import RobloxSeoText from "@/Components/RobloxSeoText.vue";
import { buildRobuxPageGraph } from "@/seo/robuxPageJsonLd.js";
import GamePassModal from "@/Components/GamePassModal.vue";
import RobuxBonus from "@/Components/RobuxBonus.vue";
import InputError from "@/Components/InputError.vue";
import RobloxTrustpilot from "@/Components/RobloxTrustpilot.vue";
import RobloxHelp from "@/Components/RobloxHelp.vue";
import RobloxTabs from "@/Components/RobloxTabs.vue";
import HowGetPromoModal from "@/Components/HowGetPromoModal.vue";

// Free tab moved to separate page

interface Props {
    min_roblox_order_amount: number | undefined;
    max_roblox_order_amount: number | undefined;
    pass_modal_visible: boolean | undefined;
    pay_amount: number | undefined;
    receive_amount: number | undefined;
    errors:
        | {
              receive_amount?: string;
          }
        | undefined;
}

const props = defineProps<Props>();
const page = usePage<any>();

const rubRate = computed(() => page.props.roblox_rates?.rub ?? '1,5');
const usdRate = computed(() => page.props.roblox_rates?.usd ?? '120');

// Real bound, not a placeholder: the "pay" field has no backend max of its own - only
// receive_amount (robux) is validated against max_roblox_order_amount server-side - so this
// mirrors that same constraint in currency terms via the displayed rate, purely so the input's
// native/ARIA max isn't left unset (which Chrome's a11y tree reports as aria-valuemax="0" despite
// a real nonzero value, rather than "unbounded").
const payAmountMax = computed(() => {
    // roblox_rates.rub is Russian comma-decimal ("1,5"), not a plain float string - see
    // seo/robuxPageJsonLd.js's lowestPrice() for the same parsing.
    const rawRate = isRu.value ? rubRate.value : usdRate.value;
    const rate = typeof rawRate === 'number' ? rawRate : parseFloat(String(rawRate ?? '').replace(',', '.'));
    return rate > 0 && props.max_roblox_order_amount
        ? Math.floor(props.max_roblox_order_amount / rate)
        : undefined;
});

const isRobloxUserSelected = computed(() => !!page.props.selected_user?.id);
const salesPaused = computed(() => !!page.props.roblox_sales_paused);
const referral = computed(
    () =>
        page.props.referral ?? {
            bonus_balance: 9999,
            credited_count: 9999,
            credited_sum: 9999,
            invitee_bonus_robux: 15,
            referrer_bonus_robux: 15,
        }
);

// Реактивные переменные для полей
const payAmount = ref<string>(props.pay_amount?.toString());
const receiveAmount = ref<string>(props.receive_amount?.toString());
const isLoading = ref<boolean>(false);
const passModalVisible = ref(props.pass_modal_visible);
const howGetPromoModalVisible = ref(false);
const inInputPromoCode = ref(page.props.promo.has);
const promoCodeValue = ref(page.props.promo.code || "");
// Populated from the get-amount axios call's 422 response - replaces the old
// $page.props.errors?.receive_amount, which only ever got set by an Inertia redirect-with-errors
// response and is never touched now that this calculator talks to the backend over axios.
const calculatorError = ref("");

const toggleInfoModalVisible = (payload: boolean) => {
    passModalVisible.value = !payload;
};

const toggleHowGetPromoModalVisible = (payload: boolean) => {
    howGetPromoModalVisible.value = !payload;
};

// Single source of truth for the fallback, matching the other call sites that already
// defaulted to 20 (config('roblox.min_roblox_order_amount')'s own default) - isReceiveAmountValid
// and the "minimum purchase" warning used to read props.min_roblox_order_amount bare, so a
// prop that failed to arrive left the button permanently disabled while everything else on the
// page (including the message text) still promised a minimum of 20.
const minRobloxOrderAmount = computed(() => props.min_roblox_order_amount ?? 20);

// Вычисляемые свойства для валидации
const isPayAmountValid = computed(() => {
    const amount = parseFloat(payAmount.value);
    return !isNaN(amount) && amount >= 0;
});

const isReceiveAmountValid = computed(() => {
    const amount = parseInt(receiveAmount.value);
    return !isNaN(amount) && amount >= minRobloxOrderAmount.value;
});

// Функция для форматирования рублей с копейками
const formatRubles = (amount: number): string => {
    return Math.round(amount).toString();
};

// Plain axios call, not an Inertia visit: an Inertia GET here pushed a
// `roblox/get-amount?...` entry onto browser history on every keystroke/quick-pick tap, so a few
// edits later "Back" stepped through past amounts instead of leaving the page - same fix as the
// header search box (see SearchInput.vue / ProductController::searchSuggestions()).
let getAmountController: AbortController | null = null;
const cancelInFlightGetAmount = () => {
    if (getAmountController) {
        getAmountController.abort();
        getAmountController = null;
    }
};

const getAmountFromBackend = (type: "pay" | "receive", amount: number) => {
    cancelInFlightGetAmount();
    const current = new AbortController();
    getAmountController = current;
    isLoading.value = true;
    calculatorError.value = "";

    axios
        .get(route("roblox.get-amount"), {
            params: { type, amount },
            signal: current.signal,
        })
        .then((response) => {
            if (getAmountController !== current) return;
            if (type === "pay") {
                receiveAmount.value =
                    response.data?.receive_amount?.toString() || "";
            } else {
                payAmount.value = response.data?.pay_amount?.toString() || "";
            }
        })
        .catch((error) => {
            if (axios.isCancel(error) || getAmountController !== current) return;
            calculatorError.value =
                error.response?.data?.errors?.receive_amount?.[0] || "";
        })
        .finally(() => {
            if (getAmountController === current) {
                getAmountController = null;
                isLoading.value = false;
            }
        });
};

// Debounced функции для обработчиков ввода
const debouncedGetAmountFromPay = debounce((amount: number) => {
    getAmountFromBackend("pay", amount);
}, 300);

const debouncedGetAmountFromReceive = debounce((amount: number) => {
    getAmountFromBackend("receive", amount);
}, 300);

// Обработчики изменения полей ввода
const onPayAmountInput = () => {
    if (payAmount.value && isPayAmountValid.value) {
        const amount = parseFloat(payAmount.value);
        debouncedGetAmountFromPay(amount);
    } else if (!payAmount.value) {
        receiveAmount.value = "";
    }
};

const onReceiveAmountInput = () => {
    if (receiveAmount.value && isReceiveAmountValid.value) {
        const amount = parseInt(receiveAmount.value);
        debouncedGetAmountFromReceive(amount);
    } else if (!receiveAmount.value) {
        payAmount.value = "";
    }
};

// Функции для кнопок быстрого выбора
const setPayAmount = (amount: number) => {
    payAmount.value = formatRubles(amount);
    getAmountFromBackend("pay", amount);
};

const setReceiveAmount = (amount: number) => {
    receiveAmount.value = amount.toString();
    getAmountFromBackend("receive", amount);
};

// Функция для перехода на экран поиска пользователя
const handleBuyRobux = () => {
    if (!isReceiveAmountValid.value) {
        return;
    }

    const robuxAmount = parseInt(receiveAmount.value);

    // Переход на страницу username с передачей количества робуксов через GET параметры
    router.get(route("roblox.username", { robux: robuxAmount }));
};

const receiveAmountError = computed(() => {
    return (
        parseInt(receiveAmount.value) < minRobloxOrderAmount.value ||
        !!props.errors?.receive_amount ||
        !!calculatorError.value
    );
});

const applyPromoCode = () => {
    router.post(
        route("roblox.promo.validate"),
        {
            code: promoCodeValue.value,
            robux_amount: parseInt(receiveAmount.value) || 0,
        },
        {
            only: ["promo", "receive_amount", "pay_amount"],
            preserveScroll: true,
        }
    );
};

const activeTab = ref(0); // kept to control local sections visibility

// --- SEO -----------------------------------------------------------------
// The page used to render `title` = "Roblox" and a generic brand-only meta
// description, so it carried no targeting for the "купить робуксы" cluster
// (405k Wordstat impressions/mo). See docs/seo/2026-07-25-linkbuilding-plan.md.

const isRu = computed(() => page.props.lang === "ru");

const metaDescription = computed(() =>
    isRu.value
        ? trans(
              "Buy Robux for Roblox at :rate Robux per ruble. Instant order processing, delivery through a Game Pass, payment with Russian cards. Minimum order :min Robux, no account password required.",
              {
                  rate: rubRate.value,
                  min: String(minRobloxOrderAmount.value),
              }
          )
        : trans(
              "Buy Robux for Roblox at :rate Robux per dollar. Instant order processing, delivery through a Game Pass. Minimum order :min Robux, no account password required.",
              {
                  rate: String(usdRate.value),
                  min: String(minRobloxOrderAmount.value),
              }
          )
);

/**
 * FAQ entries mirrored from the accordion below, so the FAQPage schema and the
 * visible block can never drift apart. Answers are taken as plain text — the
 * rendered version substitutes an HTML link for :tg, the schema must not.
 */
const faqItems = computed(() => [
    {
        q: trans("How do I buy Robux here?"),
        a: trans(
            "At the top of this page, enter the number of Robux you want to buy or the amount you want to pay in USD. Then click the “Buy Robux” button and follow the instructions."
        ),
    },
    {
        q: trans("Why can I trust AnyKod?"),
        a: trans(
            "We make money by selling robuxes, not by scamming buyers. Every buyer is important to us and we fulfill all orders and fulfill them almost instantly. Try to buy a small amount to start with (Robuxes appear in transactions as very quickly - in the Pending Robux line). Also join our groups in :tg.",
            { tg: "Telegram", vk: "VK" }
        ),
    },
    {
        q: trans("Can I get Robux for free?"),
        a: trans(
            "Yes! Join our group in :tg - we draw free Robux every day and hold various promotions!",
            { tg: "Telegram", vk: "VK" }
        ),
    },
]);

const ld = computed(() =>
    JSON.stringify(
        buildRobuxPageGraph({
            url: page.props.ziggy?.location ?? "",
            homeUrl: route("home"),
            productName: trans("Robux for Roblox"),
            description: metaDescription.value,
            currency: isRu.value ? "RUB" : "USD",
            rate: isRu.value ? rubRate.value : usdRate.value,
            minRobux: minRobloxOrderAmount.value,
            buyLabel: trans("Buy Robux"),
            faq: faqItems.value,
        })
    )
);

// Open purchases/login flow via global event handled in RobloxSidebar
const openPurchases = () => {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("roblox:open-purchases"));
    }
};

// If user isn't authorized, openPurchases instead of default actions
// Free tab actions moved to separate page
</script>

<template>
    <RobloxLayout
        :show-help="false"
        :title="$t('Buy Robux for Roblox - cheap and instant')"
        :description="metaDescription"
    >
        <Head>
            <component is="script" type="application/ld+json">
                {{ ld }}
            </component>
        </Head>
        <section class="w-full">
            <div class="w-full px-2.5 md:px-0 md:max-w-[640px] m-auto">
                <RobloxTabs active="buy" />
                <h1
                    class="pt-5 px-1 font-['Unbounded'] text-white font-medium text-xl sm:text-2xl"
                >
                    {{ $t("Buy Robux for Roblox") }}
                </h1>
                <div class="pt-5 flex gap-2 px-1">
                    <img
                        alt="tick-in-circle"
                        src="/assets/img/roblox/fire.svg"
                    />
                    <p
                        class="font-['Unbounded'] text-sky-100 font-medium text-lg"
                    >
                        {{ $t("Best rate") }}:
                        <span v-if="$page.props.lang === LOCALES.RU">
                            1 <span class="font-sans text-xl">₽</span> = {{ rubRate }}
                            Robux
                        </span>
                        <span v-else> 1 $ = {{ usdRate }} Robux </span>
                    </p>
                </div>

                <template v-if="activeTab === 0">
                    <div
                        v-if="salesPaused"
                        class="mt-5 flex flex-col items-center text-center gap-3 px-4 py-6 bg-amber-950/40 border border-amber-600/50 rounded-lg"
                    >
                        <ion-icon
                            name="pause-circle-outline"
                            class="text-amber-400"
                            style="width: 56px; height: 56px"
                        ></ion-icon>
                        <p class="font-['Unbounded'] font-semibold text-xl text-amber-300">
                            {{ $t("Robux sales are temporarily suspended") }}
                        </p>
                        <p class="font-sans text-slate-300 text-base">
                            {{ $t("We'll be back soon!") }}
                        </p>
                    </div>
                    <div
                        class="mt-5 px-3 relative py-5 bg-deep-blue shadow-[4px_4px_12px_0_rgba(0,0,0,0.3)] flex flex-col rounded-lg"
                    >
                        <img src="/assets/img/roblox/orange-robux-bg.webp" alt="orange-robux" class="absolute bottom-0 right-0 scale-x-[-1] opacity-[50%]" style="width: 120px; height: 120px" />
                        <div
                            class="w-full flex flex-col md:grid gap-5 md:grid-cols-2"
                        >
                            <div class="flex flex-col gap-2.5">
                                <p
                                    class="text-lg font-sans font-medium text-slate-200 px-2.5"
                                >
                                    {{ $t("You pay:") }}
                                </p>
                                <div
                                    class="h-[52px] w-full px-4 bg-slate-200 items-center flex rounded-lg"
                                >
                                    <img
                                        v-if="$page.props.lang === LOCALES.RU"
                                        src="/assets/img/roblox/green-bg-ruble.svg"
                                        alt="rub-icon"
                                        class="shrink-0"
                                    />
                                    <img
                                        v-else
                                        src="/assets/icons/dollar-icon.svg"
                                        alt="dollar-icon"
                                    />
                                    <input
                                        v-model="payAmount"
                                        class="bg-transparent font-sans font-bold text-2xl h-full border-none focus:ring-0 outline-hidden w-full"
                                        placeholder="0"
                                        type="number"
                                        min="0"
                                        :max="payAmountMax"
                                        @input="onPayAmountInput"
                                    />
                                </div>
                                <div
                                    class="flex gap-2.5 flex-wrap md:flex-nowrap"
                                >
                                    <template v-if="$page.props.lang === LOCALES.RU">
                                        <button
                                            v-for="amount in [700, 1000, 2000]"
                                            class="px-2 py-1.5 gap-2 shadow-[inset_1px_-3px_2px_rgba(0,0,0,0.3)] bg-gradient-to-r from-[#22C55E] to-[#FEC723] items-center rounded-lg hover:opacity-60 transition-opacity flex"
                                            @click="setPayAmount(amount)"
                                        >
                                            <img
                                                alt="ruble-filled"
                                                class="shrink-0"
                                                src="/assets/icons/ruble-filled.svg"
                                            />
                                            <span class="font-sans text-xl">
                                                {{ amount }}
                                            </span>
                                        </button>
                                    </template>
                                    <template v-else>
                                        <button
                                            v-for="amount in [5, 10, 20]"
                                            class="px-2 py-1.5 gap-2 shadow-[inset_1px_-3px_2px_rgba(0,0,0,0.3)] bg-gradient-to-r from-[#22C55E] to-[#FEC723] items-center rounded-lg hover:opacity-60 transition-opacity flex"
                                            @click="setPayAmount(amount)"
                                        >
                                            <img
                                                src="/assets/icons/dollar-icon-green.svg"
                                                alt="dollar-icon"
                                            />
                                            <span class="font-sans text-xl">
                                                {{ amount }}
                                            </span>
                                        </button>
                                    </template>
                                </div>
                            </div>
                            <div class="flex flex-col gap-2.5">
                                <div class="flex justify-between">
                                    <p
                                        class="text-lg font-sans font-medium text-slate-200 px-2.5"
                                    >
                                        {{ $t("You get:") }}
                                    </p>
                                    <RobuxBonus
                                        :robuxes="
                                            page.props.promo.bonus_robux ?? 0
                                        "
                                        color="sky-200"
                                    />
                                </div>
                                <div
                                    class="h-[52px] w-full px-4 bg-slate-200 items-center flex rounded-lg"
                                >
                                    <img
                                        src="/assets/img/roblox/orange-robux.svg"
                                        alt="roblox-icon"
                                        class="shrink-0"
                                    />
                                    <input
                                        v-model="receiveAmount"
                                        class="bg-transparent font-sans font-bold text-2xl h-full border-none focus:ring-0 outline-hidden w-full"
                                        :class="[
                                            receiveAmountError
                                                ? 'text-red-500'
                                                : 'text-black',
                                        ]"
                                        placeholder="0"
                                        type="number"
                                        :min="minRobloxOrderAmount"
                                        :max="max_roblox_order_amount"
                                        @input="onReceiveAmountInput"
                                    />
                                </div>
                                <div
                                    class="flex gap-2.5 flex-wrap md:flex-nowrap"
                                >
                                    <button
                                        v-for="amount in [700, 1000, 2000]"
                                        class="px-2 py-1.5 gap-2 bg-gradient-to-r shadow-[inset_1px_-3px_2px_rgba(0,0,0,0.3)] from-[#F74792] to-[#FEC723] items-center rounded-lg hover:opacity-60 transition-opacity flex"
                                        @click="setReceiveAmount(amount)"
                                    >
                                        <img
                                            alt="roblox-filled"
                                            class="shrink-0"
                                            src="/assets/icons/roblox-filled.svg"
                                        />
                                        <span class="font-sans text-xl">
                                            {{ amount }}
                                        </span>
                                    </button>
                                </div>
                                <div
                                    class="flex gap-3 items-center"
                                    v-if="!isReceiveAmountValid"
                                >
                                    <ion-icon
                                        name="alert-circle"
                                        class="text-red-500 shrink-0"
                                        style="width: 16px; height: 16px"
                                    ></ion-icon>
                                    <p
                                        class="font-sans font-medium text-base text-red-400"
                                    >
                                        {{
                                            $t(
                                                "MINIMUM PURCHASE :amount ROBUX",
                                                {
                                                    amount: minRobloxOrderAmount.toString(),
                                                }
                                            )
                                        }}
                                    </p>
                                </div>
                                <div
                                    class="flex gap-3 items-center"
                                    v-if="calculatorError"
                                >
                                    <ion-icon
                                        name="alert-circle"
                                        class="text-red-500 shrink-0"
                                        style="width: 16px; height: 16px"
                                    ></ion-icon>
                                    <p
                                        class="font-sans font-medium text-base text-red-400"
                                    >
                                        {{ calculatorError }}
                                    </p>
                                </div>
                                <div
                                    class="flex gap-3 items-center"
                                    v-else-if="$page.props.errors?.receive_amount"
                                >
                                    <ion-icon
                                        name="alert-circle"
                                        class="text-red-500 shrink-0"
                                        style="width: 16px; height: 16px"
                                    ></ion-icon>
                                    <p
                                        class="font-sans font-medium text-base text-red-400"
                                    >
                                        {{ $page.props.errors?.receive_amount }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            :disabled="!isReceiveAmountValid || salesPaused"
                            class="py-4 disabled:bg-violet-300 shadow-[inset_1px_-3px_2px_rgba(0,0,0,0.3)] uppercase mt-7.5 font-sans font-medium text-sm flex justify-center text-white rounded-full transition-opacity disabled:hover:opacity-100 hover:opacity-60 box-content bg-violet-500"
                            @click="handleBuyRobux"
                        >
                            {{ $t("Buy Robux") }}
                        </button>
                        <div
                            class="mt-[18px] gap-4 flex max-md:mx-auto"
                            v-if="!inInputPromoCode && !salesPaused"
                        >
                            <button
                                @click="inInputPromoCode = true"
                                class="flex text-sky-200 gap-2 underline items-end"
                            >
                                <img src="/assets/icons/promocode.svg" alt=""/>
                                {{ $t("I have a promo code") }}
                            </button>
                            <button @click="howGetPromoModalVisible = true">
                                <ion-icon name="help-circle-outline" class="text-sky-200" style="width: 16px; height: 16px"></ion-icon>
                            </button>
                        </div>
                        <div v-if="inInputPromoCode && !salesPaused">
                            <div class="mt-[18px] flex gap-2.5 h-[40px]">
                                <div
                                    class="h-full w-full px-4 max-w-[225px] bg-slate-200 border-[1px] border-slate-500 border-dashed items-center flex rounded-lg"
                                >
                                    <Promocode
                                        :class="[
                                            promoCodeValue
                                                ? 'fill-black'
                                                : 'fill-slate-500',
                                        ]"
                                    />
                                    <input
                                        v-model="promoCodeValue"
                                        class="bg-transparent font-sans font-semibold text-base h-full border-none focus:ring-0 outline-hidden w-full"
                                        :class="[
                                            promoCodeValue
                                                ? 'text-black'
                                                : 'text-slate-500',
                                        ]"
                                        :placeholder="$t('Enter code here')"
                                        type="text"
                                    />
                                </div>
                                <button
                                    :disabled="!promoCodeValue"
                                    @click="applyPromoCode"
                                    class="h-full px-5 text-[14px] disabled:text-slate-500 font-condensed font-medium disabled:bg-green-100 border-[1px] rounded-lg bg-green-300 hover:bg-green-500 text-black transition-all shadow-[inset_2px_-2px_4px_0_rgba(0,0,0,0.3)]"
                                >
                                    {{ $t("Apply") }}
                                </button>
                            </div>
                            <InputError
                                v-if="$page.props.errors?.code"
                                :message="$page.props.errors?.code"
                                class="mt-3 flex gap-3 items-start"
                            />
                            <div
                                v-else-if="page.props.promo.message"
                                class="mt-3 flex gap-3 items-start"
                            >
                                <ion-icon
                                    name="alert-circle"
                                    class="text-sky-200 h-5 w-5"
                                    style="height: 20px; width: 20px"
                                ></ion-icon>
                                <p
                                    class="font-condensed leading-1 font-medium text-[14px] text-sky-200"
                                >
                                    {{ page.props.promo.message }}
                                </p>
                            </div>
                            <button
                                @click="howGetPromoModalVisible = true"
                                class="flex m-auto text-sky-200 mt-5 gap-2 underline items-end"
                            >
                                <img src="/assets/icons/promocode.svg" alt=""/>
                                {{ $t('How to get a promo code?') }}
                            </button>
                        </div>

                        <p
                            class="font-sans text-sm px-2.5 text-slate-500 mt-5"
                            v-html="
                                $t(
                                    `We fulfill your order immediately - the robuxes will appear in your Pending transactions right away, Roblox credits them within 5-7 days. This is Roblox's own rule for all Game Pass transfers.`
                                )
                            "
                        ></p>
                        <div
                            class="flex pt-5 w-full justify-start items-center border-t border-dashed border-gray-500 mt-2.5 pb-2.5"
                        >
                            <p
                                class="text-orange-500 uppercase font-sans font-medium text-sm px-1"
                            >
                                {{ $t("Available now:") }}
                            </p>
                            <div class="flex items-center ml-[6px]">
                                <img
                                    src="/assets/img/roblox/orange-2d-robux.svg"
                                    alt="roblox-icon"
                                    class="shrink-0 w-5 h-5"
                                    style="width: 20px; height: 20px"
                                />
                                <p
                                    class="text-orange-500 uppercase font-sans font-medium text-[20px] px-1"
                                >
                                    {{ $page.props.roblox_rates?.available ?? '1 500 000' }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="mt-5">
                        <RobloxTrustpilot />
                    </div>
                    <div class="pt-5 flex items-center gap-2 px-1">
                        <img
                            alt="tick-in-circle"
                            src="/assets/img/roblox/target.webp"
                            style="width: 24px; height: 24px"
                        />
                        <p
                            class="font-['Unbounded'] text-sky-200 font-medium text-lg"
                        >
                            {{
                                $t(
                                    "The best rate among all competitor web-sites"
                                )
                            }}
                        </p>
                    </div>
                    <div class="pt-5 flex items-center gap-2 px-1">
                        <img
                            alt="tick-in-circle"
                            src="/assets/img/roblox/lightning.webp"
                            style="width: 14px; height: 32px"
                        />
                        <p
                            class="font-['Unbounded'] text-sky-200 font-medium text-lg"
                        >
                            {{ $t("Instant shipment") }}
                        </p>
                    </div>
                    <p
                        class="mt-7 px-1 flex gap-1 items-center font-sans font-medium text-sky-200 text-sm"
                    >
                        <img
                            alt="latest-purchases"
                            src="/assets/icons/latest-purchases.svg"
                        />
                        {{ $t("Recent Purchases:") }}
                    </p>
                </template>
            </div>
            <LatestPurchasesRoblox v-if="activeTab === 0" />
            <RobloxDraw />
            <RobloxMarketplacePromo />
            <div v-if="activeTab === 0" class="mt-9 w-full">
                <h2
                    class="font-['Unbounded'] text-white text-2xl sm:text-2.5xl text-center mb-16 px-5"
                >
                    {{ $t("FAQ") }}
                </h2>
                <div
                    class="max-w-[654px] w-full flex flex-col items-center m-auto px-5"
                >
                    <FAQAccordion>
                        <template #question>
                            {{ $t("How do I buy Robux here?") }}
                        </template>
                        <template #answer>
                            {{
                                $t(
                                    "At the top of this page, enter the number of Robux you want to buy or the amount you want to pay in USD. Then click the “Buy Robux” button and follow the instructions."
                                )
                            }}
                        </template>
                    </FAQAccordion>
                    <FAQAccordion>
                        <template #question>
                            {{ $t("Why can I trust AnyKod?") }}
                        </template>
                        <template #answer>
                            <span
                                v-html="
                                    $t(
                                        'We make money by selling robuxes, not by scamming buyers. Every buyer is important to us and we fulfill all orders and fulfill them almost instantly. Try to buy a small amount to start with (Robuxes appear in transactions as very quickly - in the Pending Robux line). Also join our groups in :tg.',
                                        {
                                            tg: `<a href='${tgUrl}' target='_blank'  class='text-blue-400 underline'>Telegram</a>`,
                                            vk: `<a href='${vkUrl}' target='_blank'  class='text-blue-400 underline'>VK</a>`,
                                        }
                                    )
                                "
                            >
                            </span>
                        </template>
                    </FAQAccordion>
                    <FAQAccordion>
                        <template #question>
                            {{ $t("Can I get Robux for free?") }}
                        </template>
                        <template #answer>
                            <span
                                v-html="
                                    $t(
                                        `Yes! Join our group in :tg - we draw free Robux every day and hold various promotions!`,
                                        {
                                            tg: `<a href='${tgUrl}' target='_blank' class='text-blue-400 underline'>Telegram</a>`,
                                            vk: `<a href='${vkUrl}' target='_blank'  class='text-blue-400 underline'>VK</a>`,
                                        }
                                    )
                                "
                            >
                            </span>
                        </template>
                    </FAQAccordion>
                </div>
            </div>
            <RobloxSeoText v-if="activeTab === 0" />
        </section>
        <RobloxHelp v-if="activeTab === 0" />
    </RobloxLayout>
    <GamePassModal
        @on-toggle="toggleInfoModalVisible"
        :is-open="passModalVisible"
    />
    <HowGetPromoModal :is-open="howGetPromoModalVisible" @on-toggle="toggleHowGetPromoModalVisible"/>
</template>
